import { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card } from '@tremor/react';
import { useCurrentUserRole } from '../../hooks/useCurrentUserRole';
import { hasRoleLevel } from '../../utils/roles';
import { apiErrorMessage } from '../../utils/notifyError';

const API_HOST = process.env.REACT_APP_API_HOST;

type ActivationResult = { plan?: string; tier?: number | string };

/**
 * AppSumo redirects buyers here with `?code=`. A signed-in admin activates
 * the licence for their workspace: the API exchanges the code with AppSumo,
 * links the licence to the company and sets its plan.
 */
export default function AppSumo() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const { level, isLoading: roleLoading } = useCurrentUserRole();
  const isAdmin = hasRoleLevel(level, 'admin');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ActivationResult | null>(null);

  const activate = async () => {
    if (!code) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_HOST}/v1/appsumo/activate`, {
        code,
      });
      setResult({ plan: data?.plan, tier: data?.tier });
    } catch (err) {
      setError(
        apiErrorMessage(err, 'We could not activate this licence. Please try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center py-12">
      <Card className="w-full max-w-lg">
        <h2 className="text-xl font-semibold text-gray-900">
          Activate your AppSumo deal
        </h2>

        {!code && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            AppSumo authorisation code not found. Please open the activation
            link from your AppSumo account again.
          </p>
        )}

        {code && !result && (
          <>
            <p className="mt-3 text-sm text-gray-600">
              Activate the AppSumo licence for your workspace. Your plan is
              updated to match the licence tier.
            </p>
            {!roleLoading && !isAdmin && (
              <p className="mt-3 text-sm text-amber-700" role="alert">
                Only a workspace admin can activate a licence. Ask an admin to
                open this link.
              </p>
            )}
            {error && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <Button
              className="mt-6 w-full"
              color="red"
              loading={submitting}
              disabled={submitting || roleLoading || !isAdmin}
              onClick={activate}
            >
              Activate AppSumo licence for your workspace
            </Button>
          </>
        )}

        {result && (
          <div className="mt-3 text-sm text-gray-700" role="status">
            <p>Your AppSumo licence is active.</p>
            {result.plan && (
              <p className="mt-1">
                Plan: <span className="font-medium">{result.plan}</span>
                {result.tier !== undefined && result.tier !== null
                  ? ` (tier ${result.tier})`
                  : ''}
              </p>
            )}
            <Link to="/" className="mt-4 inline-block text-red-600 underline">
              Go to your dashboard
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
